import { useMemo, useState } from 'react';
import ActiveOrders from './active-orders/ActiveOrders';
import DraftOrders from './draft-orders/DraftOrders';
import useStore from '@/store/store';
import useWindowSize from '@/utils/useWindowSize';
import { useIsMobile } from '@/utils/useIsMobile';
import { lazyWithSuspense } from '@/shared/lazyWithSuspense';
import { DELAY_OPTIONS, DISPLAY_OPTIONS } from '@/utils/constants';
import { canSeeShopFees } from '@/utils/funcs';
import Tabs from '@/ui/tabs/Tabs';
import { SearchOrderFields } from './Search-Orders-Fields';
import s from './live.module.css';

import { useOperationStatus } from './live/hooks/useOperationStatus';
import { useOrdersData } from './live/hooks/useOrdersData';
import { useAllDeliveries } from './live/hooks/useAllDeliveries';
import { useShopSearch } from './live/hooks/useShopSearch';
import { useClientSearch } from './live/hooks/useClientSearch';
import { useOrderCompletion } from './live/hooks/useOrderCompletion';
import { useGenericOrderModal } from './live/hooks/useGenericOrderModal';
import { useDeliveryAssignment } from './live/hooks/useDeliveryAssignment';
import { useNewOrderForm } from './live/hooks/useNewOrderForm';
import { useLiveSocket } from './live/hooks/useLiveSocket';
import { useOrderFilter } from './live/hooks/useOrderFilter';
import { useActivityFeed } from './live/hooks/useActivityFeed';
import { useMapLayers, MAP_LAYERS } from './live/hooks/useMapLayers';

import { LiveMapSection } from './live/components/LiveMapSection';
import { WarningBanner } from './live/components/WarningBanner';
import { SelectedShopChip } from './live/components/SelectedShopChip';
import { ShopSearchBox } from './live/components/ShopSearchBox';
import { ClientSelector } from './live/components/ClientSelector';
import { SimpleOrderFields } from './live/components/SimpleOrderFields';
import { OrderFormFields } from './live/components/OrderFormFields';
import { LiveModals } from './live/components/LiveModals';
import { ActivityFeed } from './live/components/activity/ActivityFeed';

const Deliveries = lazyWithSuspense(() => import('./deliveries/Deliveries'), null, null);

// Mutates the shared constant, so guard against inserting twice on an HMR re-eval.
if (process.env.NODE_ENV === 'development' && !DELAY_OPTIONS.some(it => it.value === 0.1)) {
	DELAY_OPTIONS.splice(1, 0, { label: 0.1, value: 0.1 });
}

export const PageLive = () => {
	const isMobile = useIsMobile();
	const size = useWindowSize();

	const { user, setUser } = useStore();
	const isMasterOrAdmin = user?.role === 'admin' || user?.role === 'master';
	// Unlike the rider fee, the charge is the shop's own bill, so a shop sees it
	// on the board too — unless the city set hideShopFeesFromShops. An admin can
	// also be hidden from it one by one (admin.hideShopFees).
	const showShopFee = canSeeShopFees(user);
	// A shop normally cannot open the order modal at all; allowChangeOrderDelay
	// lets it in, and changing the preparation time is the only thing it may do
	// once there — so this doubles as the "may open the modal" answer.
	const canChangeOrderDelay = isMasterOrAdmin || !!user?.allowChangeOrderDelay;

	const [displayOption, setDisplayOption] = useState(
		localStorage.getItem('displayOption') || DISPLAY_OPTIONS.FULL,
	);
	// Google bills every map it creates. A phone reloads pages it put in the
	// background, so a map shown by default there is paid for over and over;
	// on a phone it starts hidden every time and opens on request
	// («Εμφάνιση χάρτη»), deliberately not remembered. A computer keeps
	// «Προβολή» as before.
	const [phoneMapOpen, setPhoneMapOpen] = useState(false);
	const mapShown = isMobile ? phoneMapOpen : displayOption !== DISPLAY_OPTIONS.ORDERS;
	// Persisted like its neighbour «Προβολή»: a dispatcher who put the timeline
	// away does not want it back on the next reload. Off by default — the board
	// is what the page is for.
	//
	// One preference PER DEVICE, and the phone starts from off however the desk
	// is set. The feed sits under the map, which on a desktop is a column beside
	// the board and on a phone is a screenful below it — so a preference that
	// costs nothing at a desk pushes the orders out of view on the way to a
	// delivery. Desktop keeps the original key, so an existing setting survives.
	const activityKey = isMobile ? 'activity-feed-mobile' : 'activity-feed';
	const [activityVisible, setActivityVisible] = useState(
		() => localStorage.getItem(activityKey) === 'on',
	);
	const [newOrderMobileVisible, setNewOrderMobileVisible] = useState(false);

	const operationStatus = useOperationStatus(user);
	const ordersData = useOrdersData({ user, isMasterOrAdmin });
	const allDeliveriesData = useAllDeliveries({ user, isMasterOrAdmin });
	const shop = useShopSearch({ user, isMasterOrAdmin });
	const client = useClientSearch({
		user,
		activeOrders: ordersData.activeOrders,
		setActiveOrders: ordersData.setActiveOrders,
	});
	const orderCompletion = useOrderCompletion({ user, activeOrders: ordersData.activeOrders });
	const genericOrderModal = useGenericOrderModal({
		isMasterOrAdmin,
		onCloseCancelModal: orderCompletion.onCloseCancelModal,
	});
	const deliveryAssignment = useDeliveryAssignment({
		allShops: shop.allShops,
		closeGenericOrderModal: genericOrderModal.onCloseGenericOrderModal,
	});
	const newOrderForm = useNewOrderForm({
		user,
		isMasterOrAdmin,
		shop,
		client,
		setOperating: operationStatus.setOperating,
		setNotOperatingReason: operationStatus.setNotOperatingReason,
		setWarningMessage: operationStatus.setWarningMessage,
		setNewOrderMobileVisible,
	});
	const { positions, socket } = useLiveSocket({
		user,
		setUser,
		isMasterOrAdmin,
		activeOrdersRef: ordersData.activeOrdersRef,
		setActiveOrders: ordersData.setActiveOrders,
		draftOrdersRef: ordersData.draftOrdersRef,
		setDraftOrders: ordersData.setDraftOrders,
		sortOrders: ordersData.sortOrders,
		allDeliveriesRef: allDeliveriesData.allDeliveriesRef,
		setAllDeliveries: allDeliveriesData.setAllDeliveries,
		orderToAssignDeliveryRef: deliveryAssignment.orderToAssignDeliveryRef,
		setOrderToAssignDelivery: deliveryAssignment.setOrderToAssignDelivery,
		setAssignDeliveryModalOpen: deliveryAssignment.setAssignDeliveryModalOpen,
	});
	const orderFilter = useOrderFilter(ordersData.activeOrders);
	// Admin-only: the rows carry rider names, every shop's orders and the whole
	// city's shift activity. The backend guards both the socket and the endpoint,
	// so this is the third lock rather than the only one.
	const activity = useActivityFeed({ user, isMasterOrAdmin, socket });
	const { layers, toggleLayer } = useMapLayers(user);

	const shopPositions = useMemo(() => {
		if (
			!shop.allShops?.length ||
			!ordersData.activeOrders?.length ||
			!isMasterOrAdmin ||
			!layers[MAP_LAYERS.SHOPS]
		) {
			return {};
		}

		const positions = {};

		shop.allShops.forEach(sh => {
			const id = `s-${sh.id}`;
			if (positions[id] || ordersData.activeOrders.some(o => o.shopId === sh.id)) {
				positions[id] = {
					name: sh.name,
					coords: { latitude: sh.address.lat, longitude: sh.address.lng },
					type: 'shop',
				};
			}
		});

		return positions;
	}, [ordersData.activeOrders, shop.allShops, isMasterOrAdmin, layers]);

	const switchDisplay = () => {
		if (displayOption === DISPLAY_OPTIONS.FULL) {
			setDisplayOption(DISPLAY_OPTIONS.ORDERS);
			localStorage.setItem('displayOption', DISPLAY_OPTIONS.ORDERS);
		} else if (displayOption === DISPLAY_OPTIONS.ORDERS) {
			setDisplayOption(DISPLAY_OPTIONS.FULL);
			localStorage.setItem('displayOption', DISPLAY_OPTIONS.FULL);
		} else {
			setDisplayOption(DISPLAY_OPTIONS.FULL);
			localStorage.setItem('displayOption', DISPLAY_OPTIONS.FULL);
		}
	};

	const toggleActivity = () => {
		setActivityVisible(prev => {
			localStorage.setItem(activityKey, prev ? 'off' : 'on');
			return !prev;
		});
	};

	const closeAllModals = () => {
		client.onCloseClientModal();
		client.onCloseEditClientModal();
		genericOrderModal.onCloseGenericOrderModal();
		orderCompletion.onCloseCompleteModal();
	};

	const mapWidth =
		size.width > 1500 ? size.width - 1200 : size.width > 860 ? 310 : size.width * 0.95;
	const mapHeight = size.width > 860 ? '80dvh' : '40dvh';

	const searchOrderFieldsComponent = isMobile ? null : (
		<SearchOrderFields
			orderSearchText={orderFilter.orderSearchText}
			setOrderFieldsToSearch={orderFilter.setOrderFieldsToSearch}
			onChange={e => orderFilter.setOrderSearchText(e)}
			orderFieldsToSearch={orderFilter.orderFieldsToSearch}
		/>
	);

	return (
		<div className={s.outerContainer}>
			<LiveMapSection
				isMasterOrAdmin={isMasterOrAdmin}
				switchDisplay={switchDisplay}
				user={user}
				mapShown={mapShown}
				isMobile={isMobile}
				togglePhoneMap={() => setPhoneMapOpen(open => !open)}
				mapWidth={mapWidth}
				mapHeight={mapHeight}
				positions={positions}
				shopPositions={shopPositions}
				layers={layers}
				toggleLayer={toggleLayer}
				finalActiveOrders={orderFilter.finalActiveOrders}
				activityVisible={activityVisible}
				toggleActivity={toggleActivity}
				activityFeed={
					<ActivityFeed
						events={activity.events}
						loading={activity.loading}
						error={activity.error}
						allDeliveries={allDeliveriesData.allDeliveries}
						range={activity.range}
						setRange={activity.setRange}
						filteringByDate={activity.filteringByDate}
						retentionDays={activity.retentionDays}
					/>
				}
			/>

			<div className={s.container}>
				<WarningBanner
					warningMessage={operationStatus.warningMessage}
					warningMessageVisible={operationStatus.warningMessageVisible}
					setWarningMessageVisible={operationStatus.setWarningMessageVisible}
					isMasterOrAdmin={isMasterOrAdmin}
				/>

				<SelectedShopChip
					isOperating={operationStatus.isOperating}
					isMasterOrAdmin={isMasterOrAdmin}
					selectedShop={shop.selectedShop}
					onRemoveShop={newOrderForm.onRemoveShop}
				/>

				<ClientSelector
					isOperating={operationStatus.isOperating}
					isMasterOrAdmin={isMasterOrAdmin}
					selectedShop={shop.selectedShop}
					selectedClient={client.selectedClient}
					asSimpleOrder={newOrderForm.asSimpleOrder}
					requiredOrderFields={newOrderForm.requiredOrderFields}
					freeClientAllowed={newOrderForm.freeClientAllowed}
					clientSearchText={client.clientSearchText}
					setClientSearchText={client.setClientSearchText}
					onFocusClient={client.onFocusClient}
					isSearchingClient={client.isSearchingClient}
					foundClients={client.foundClients}
					dropdownRef={client.dropdownRef}
					onClientSelect={client.onClientSelect}
					openClientModal={client.openClientModal}
					addButtonsDisabled={newOrderForm.addButtonsDisabled}
					onSetAsSimpleOrder={newOrderForm.onSetAsSimpleOrder}
					setClientPhone={newOrderForm.setClientPhone}
					validatePhone={newOrderForm.validatePhone}
					onRemoveClient={client.onRemoveClient}
					selectedAddressIndex={client.selectedAddressIndex}
					setSelectedAddressIndex={client.setSelectedAddressIndex}
				/>

				<SimpleOrderFields
					user={user}
					asSimpleOrder={newOrderForm.asSimpleOrder}
					requiredOrderFields={newOrderForm.requiredOrderFields}
					clientName={newOrderForm.clientName}
					setClientName={newOrderForm.setClientName}
					validateName={newOrderForm.validateName}
					errorName={newOrderForm.errorName}
					onFocusShop={shop.onFocusShop}
					clientPhone={newOrderForm.clientPhone}
					setClientPhone={newOrderForm.setClientPhone}
					validatePhone={newOrderForm.validatePhone}
					errorPhone={newOrderForm.errorPhone}
					clientAddress={newOrderForm.clientAddress}
					setClientAddress={newOrderForm.setClientAddress}
					validateAddress={newOrderForm.validateAddress}
					errorAddress={newOrderForm.errorAddress}
					clientLocation={newOrderForm.clientLocation}
					setClientLocation={newOrderForm.setClientLocation}
					setErrorLocation={newOrderForm.setErrorLocation}
					errorLocation={newOrderForm.errorLocation}
				/>

				<OrderFormFields
					selectedClient={client.selectedClient}
					asSimpleOrder={newOrderForm.asSimpleOrder}
					requiredOrderFields={newOrderForm.requiredOrderFields}
					price={newOrderForm.price}
					setPrice={newOrderForm.setPrice}
					validatePrice={newOrderForm.validatePrice}
					errorPrice={newOrderForm.errorPrice}
					size={size}
					paymentType={newOrderForm.paymentType}
					setPaymentType={newOrderForm.setPaymentType}
					comments={newOrderForm.comments}
					setComments={newOrderForm.setComments}
					areas={newOrderForm.areas}
					isAutoArea={newOrderForm.isAutoArea}
					pinArea={newOrderForm.pinArea}
					selectedAreaIdx={newOrderForm.selectedAreaIdx}
					setSelectedAreaIdx={newOrderForm.setSelectedAreaIdx}
					areaPriceProm={newOrderForm.areaPriceProm}
					setAreaPriceProm={newOrderForm.setAreaPriceProm}
					areaShopFee={newOrderForm.areaShopFee}
					setAreaShopFee={newOrderForm.setAreaShopFee}
					selectedShop={shop.selectedShop}
					isMasterOrAdmin={isMasterOrAdmin}
					delay={newOrderForm.delay}
					delayRef={newOrderForm.delayRef}
					onDelaySelect={newOrderForm.onDelaySelect}
					delayOptionsFiltered={newOrderForm.delayOptionsFiltered}
					isOperating={operationStatus.isOperating}
					user={user}
					submitOrder={newOrderForm.submitOrder}
					saveDisabled={newOrderForm.saveDisabled}
					isSendingOrder={newOrderForm.isSendingOrder}
					notOperatingReason={operationStatus.notOperatingReason}
				/>

				<div className='relative flex flex-row'>
					{!newOrderMobileVisible && isMasterOrAdmin && !newOrderMobileVisible && (
						<div
							style={{
								position: 'absolute',
								display: 'flex',
								flexDirection: 'row',
								alignItems: 'center',
								columnGap: 8,
								left: 216,
								height: 38,
								marginTop: 16,
								fontSize: 15,
								lineHeight: 1.1,
							}}
						>
							{size.width > 1500 ? searchOrderFieldsComponent : null}
						</div>
					)}

					<ShopSearchBox
						isOperating={operationStatus.isOperating}
						isMasterOrAdmin={isMasterOrAdmin}
						selectedShop={shop.selectedShop}
						size={size}
						shopSearchText={shop.shopSearchText}
						onShopSearch={shop.onShopSearch}
						onFocusShop={shop.onFocusShop}
						setNewOrderMobileVisible={setNewOrderMobileVisible}
						setShopSearchText={shop.setShopSearchText}
						dropdownShopRef={shop.dropdownShopRef}
						onShopSelect={shop.onShopSelect}
						foundShops={shop.foundShops}
					/>

					<Tabs
						styleContainer={{ marginTop: 12 }}
						middleComponent={size.width <= 1500 ? searchOrderFieldsComponent : null}
						tabItems={[
							{
								label: `Live ${ordersData.activeOrders?.length || '0'}`,
								content: (
									<ActiveOrders
										mapVisible={
											isMasterOrAdmin &&
											user?.cityLocation?.location &&
											mapShown &&
											size.width < 860
										}
										data={orderFilter.finalActiveOrders || []}
										showAreaPromLabel={window?.location?.href?.includes('solvd')}
										showShop={isMasterOrAdmin}
										showShopFee={showShopFee}
										canOpenOrderModal={canChangeOrderDelay}
										continuousOrderDisplayTime={user?.cityLocation?.continuousOrderDisplayTime}
										selectShopForDelivery={deliveryAssignment.selectShopForDelivery}
										openGenericOrderModal={genericOrderModal.openGenericOrderModal}
										openOrderDeliverySelectedModal={
											deliveryAssignment.openOrderDeliverySelectedModal
										}
										withDeliveryPhoneVisible={user?.cityLocation?.withDeliveryPhoneVisible}
										onOpenClient={client.openEditClientModal}
										colorZonesCity={isMasterOrAdmin ? user?.cityLocation : null}
									/>
								),
							},
							{
								label: `Drafts ${ordersData.draftOrders?.length || '0'}`,
								content: (
									<DraftOrders
										onCancelDraftOrder={genericOrderModal.onCancelDraftOrder}
										mapVisible={
											isMasterOrAdmin &&
											user?.cityLocation?.location &&
											mapShown &&
											size.width < 860
										}
										data={ordersData.draftOrders || []}
										showShop={isMasterOrAdmin}
										continuousOrderDisplayTime={user?.cityLocation?.continuousOrderDisplayTime}
										onOpenClient={client.openEditClientModal}
									/>
								),
							},
						]}
					/>
				</div>
			</div>

			<LiveModals
				isMasterOrAdmin={isMasterOrAdmin}
				isClientModalOpen={client.isClientModalOpen}
				isEditClientModalOpen={client.isEditClientModalOpen}
				isGenericOrderModalOpen={genericOrderModal.isGenericOrderModalOpen}
				isCompleteOrderModalOpen={orderCompletion.isCompleteOrderModalOpen}
				closeAllModals={closeAllModals}
				selectedClient={client.selectedClient}
				clientSearchText={client.clientSearchText}
				onCloseClientModal={client.onCloseClientModal}
				onNewOrEditClient={client.onNewOrEditClient}
				editSelectedClient={client.editSelectedClient}
				onCloseEditClientModal={client.onCloseEditClientModal}
				onSuccessClientEditCb={client.onSuccessClientEditCb}
				feeList={orderCompletion.feeList}
				customFee={orderCompletion.customFee}
				changeCustomFee={orderCompletion.changeCustomFee}
				selectedFeeIdx={orderCompletion.selectedFeeIdx}
				changeSelectedFeeIdx={orderCompletion.changeSelectedFeeIdx}
				customFeeSelected={orderCompletion.customFeeSelected}
				completeOrder={orderCompletion.completeOrder}
				onCloseCompleteModal={orderCompletion.onCloseCompleteModal}
				onCloseGenericOrderModal={genericOrderModal.onCloseGenericOrderModal}
				onCancelOrder={genericOrderModal.onCancelOrder}
				openCompleteModal={orderId => {
					genericOrderModal.onCloseGenericOrderModal();
					orderCompletion.openCompleteModal(orderId);
				}}
				onRemoveOrderDelivery={deliveryAssignment.onRemoveOrderDelivery}
				selectedGenericOrderId={genericOrderModal.selectedGenericOrderId}
				activeOrders={ordersData.activeOrders}
				areas={newOrderForm.areas}
				user={user}
				selectedShop={shop.selectedShop}
				onEditLiveOrder={genericOrderModal.onEditLiveOrder}
				onChangeOrderDelay={genericOrderModal.onChangeOrderDelay}
			/>

			<Deliveries
				order={deliveryAssignment.orderToAssignDelivery}
				assignDeliveryModalOpen={deliveryAssignment.assignDeliveryModalOpen}
				setAssignDeliveryModalOpen={deliveryAssignment.setAssignDeliveryModalOpen}
				allDeliveries={allDeliveriesData.allDeliveries}
				onDeliverySelect={deliveryAssignment.onDeliverySelect}
				onRemoveOrderDelivery={deliveryAssignment.onRemoveOrderDelivery}
				shopToAssignDelivery={deliveryAssignment.shopToAssignDelivery}
				positions={positions}
				withClock={user?.cityLocation?.withClock}
			/>
			{user?.role === 'shop' && user.cityLocation?.contactPhone && (
				<a
					href={`viber://chat?number=+30${user.cityLocation.contactPhone}`}
					style={{ display: 'flex', flexDirection: 'column', alignSelf: 'flex-start' }}
				>
					<div style={{ color: '#0f9b9d', paddingTop: 10, paddingBottom: 10, paddingRight: 8 }}>
						Επικοινωνία
					</div>
				</a>
			)}
		</div>
	);
};
